import OpenAI from 'openai';
import { CHATBOT_SYSTEM_PROMPT } from '@/data/chatbotKnowledge';
const getLiaraConfig = () => {
    const baseURL = import.meta.env.VITE_LIARA_BASE_URL || '';
    const apiKey = import.meta.env.VITE_LIARA_API_KEY || '';
    const model = import.meta.env.VITE_LIARA_MODEL || 'google/gemini-2.0-flash-001';
    return { baseURL, apiKey, model };
};
const { baseURL, apiKey, model } = getLiaraConfig();
const openai = new OpenAI({
    baseURL: baseURL || undefined,
    apiKey: apiKey || import.meta.env.VITE_OPENAI_API_KEY || '',
    dangerouslyAllowBrowser: true
});
export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp?: Date;
}
export interface ChatResponse {
    message: string;
    success: boolean;
    error?: string;
}
class LiaraGeminiService {
    private conversationHistory: ChatMessage[] = [];
    private maxHistoryLength = 10;
    private config: {
        baseURL: string;
        apiKey: string;
        model: string;
    };
    constructor() {
        this.config = getLiaraConfig();
        this.conversationHistory = [
            {
                role: 'system',
                content: CHATBOT_SYSTEM_PROMPT,
                timestamp: new Date()
            }
        ];
    }
    isConfigured(): boolean {
        return !!(this.config.baseURL && this.config.apiKey);
    }
    async sendMessage(userMessage: string): Promise<ChatResponse> {
        try {
            if (!this.isConfigured()) {
                return this.getFallbackResponse(userMessage);
            }
            const userMsg: ChatMessage = {
                role: 'user',
                content: userMessage,
                timestamp: new Date()
            };
            this.conversationHistory.push(userMsg);
            if (this.conversationHistory.length > this.maxHistoryLength + 1) {
                this.conversationHistory = [
                    this.conversationHistory[0],
                    ...this.conversationHistory.slice(-this.maxHistoryLength)
                ];
            }
            const completion = await openai.chat.completions.create({
                model: this.config.model,
                messages: this.conversationHistory.map(msg => ({
                    role: msg.role,
                    content: msg.content
                })),
                max_tokens: 800,
                temperature: 0.7,
                presence_penalty: 0.1,
                frequency_penalty: 0.1
            });
            const assistantMessage = completion.choices[0]?.message?.content || 'متأسفانه نتوانستم پاسخ مناسبی تولید کنم. لطفاً دوباره تلاش کنید.';
            const assistantMsg: ChatMessage = {
                role: 'assistant',
                content: assistantMessage,
                timestamp: new Date()
            };
            this.conversationHistory.push(assistantMsg);
            return {
                message: assistantMessage,
                success: true
            };
        }
        catch (error: any) {
            console.error('Liara Gemini API Error:', error);
            let errorMessage = 'خطایی در ارتباط با سرور رخ داده است.';
            if (error?.error?.code === 'invalid_api_key' || error?.status === 401) {
                errorMessage = 'کلید API معتبر نیست.';
            }
            else if (error?.error?.code === 'insufficient_quota' || error?.status === 429) {
                errorMessage = 'سهمیه API تمام شده است.';
            }
            else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
                errorMessage = 'خطای شبکه. لطفاً اتصال اینترنت خود را بررسی کنید.';
            }
            return {
                message: errorMessage,
                success: false,
                error: error.message
            };
        }
    }
    getConversationHistory(): ChatMessage[] {
        return this.conversationHistory.filter(msg => msg.role !== 'system');
    }
    clearHistory(): void {
        this.conversationHistory = [this.conversationHistory[0]];
    }
    getFallbackResponse(userMessage: string): ChatResponse {
        const message = userMessage.toLowerCase();
        if (message.includes('سلام') || message.includes('درود')) {
            return {
                message: 'سلام! خوش آمدید به آزمایشگاه سلامت. چطور می‌تونم کمکتون کنم؟',
                success: true
            };
        }
        if (message.includes('ساعت کار') || message.includes('زمان')) {
            return {
                message: 'آزمایشگاه از شنبه تا چهارشنبه از ساعت 06:00 تا 21:00، پنج‌شنبه 06:00 تا 20:30 و جمعه‌ها تعطیل است.',
                success: true
            };
        }
        if (message.includes('تلفن') || message.includes('تماس')) {
            return {
                message: 'شماره تماس آزمایشگاه: 021-46833010 و 021-46833011',
                success: true
            };
        }
        if (message.includes('آدرس') || message.includes('نشانی')) {
            return {
                message: 'آدرس آزمایشگاه: شهرقدس، میدان مصلی',
                success: true
            };
        }
        if (message.includes('نتیجه') || message.includes('جواب')) {
            return {
                message: 'برای دریافت نتیجه آزمایش به آدرس http://93.114.111.53:8086/Login مراجعه کنید.',
                success: true
            };
        }
        return {
            message: 'متأسفانه در حال حاضر امکان پاسخ‌دهی وجود ندارد. لطفاً با شماره 021-46833010 تماس بگیرید.',
            success: false
        };
    }
}
export const openaiService = new LiaraGeminiService();
export default openaiService;
